import { decodeJwtPayload, pkceChallenge, randomToken } from "./pkce";
import type { AuthClient, Claims, Session } from "./types";

export interface AuthConfig {
  /** Cognito managed-login origin, e.g. `https://auth.allergy123.com` or the hosted prefix domain. */
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scopes?: string[];
  /** Defaults to sessionStorage: per-tab, cleared when the tab closes. */
  storage?: Storage;
  /** Test seam. */
  fetch?: typeof fetch;
  navigate?: (url: string) => void;
}

const TX_KEY = "a123.auth.tx";
const SESSION_KEY = "a123.auth.session";
const REFRESH_SKEW_MS = 30_000;

interface Transaction {
  verifier: string;
  state: string;
  returnTo: string;
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

function claimsFrom(idToken: string): Claims {
  const p = decodeJwtPayload(idToken);
  const groups = Array.isArray(p["cognito:groups"]) ? (p["cognito:groups"] as string[]) : [];
  // The pool stores given_name and family_name; it never issues `name`.
  const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
  const name = str(p.name) ?? ([str(p.given_name), str(p.family_name)].filter(Boolean).join(" ") || undefined);
  return { sub: String(p.sub), email: str(p.email), name, groups };
}

/**
 * Authorization-code flow with PKCE against Cognito's OAuth 2.0 endpoints
 * (ADR 0004, ADR 0010). No client secret exists. Tokens live in memory with a
 * per-tab copy in sessionStorage so a reload does not force a new sign-in.
 */
export function createAuthClient(config: AuthConfig): AuthClient {
  const storage = config.storage ?? (typeof sessionStorage !== "undefined" ? sessionStorage : undefined);
  const doFetch = config.fetch ?? fetch;
  const navigate = config.navigate ?? ((url: string) => window.location.assign(url));
  const scopes = config.scopes ?? ["openid", "email", "profile"];
  const listeners = new Set<(s: Session | null) => void>();
  let session: Session | null = restore();
  let refreshing: Promise<Session | null> | null = null;
  let callback: { url: string; result: Promise<{ returnTo: string }> } | null = null;

  function restore(): Session | null {
    try {
      const raw = storage?.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }

  function commit(next: Session | null) {
    session = next;
    try {
      if (next) storage?.setItem(SESSION_KEY, JSON.stringify(next));
      else storage?.removeItem(SESSION_KEY);
    } catch {
      /* storage may be unavailable; memory still works for this page */
    }
    for (const l of listeners) l(next);
  }

  async function exchange(body: Record<string, string>): Promise<Session> {
    const res = await doFetch(`${config.authority}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: config.clientId, ...body }).toString(),
    });
    if (!res.ok) {
      let code: string | undefined;
      try {
        code = ((await res.json()) as { error?: string }).error;
      } catch {
        /* not JSON */
      }
      throw new TokenError(res.status, code);
    }
    const t = (await res.json()) as TokenResponse;
    return {
      accessToken: t.access_token,
      idToken: t.id_token,
      refreshToken: t.refresh_token ?? session?.refreshToken,
      expiresAt: Date.now() + t.expires_in * 1000,
      claims: claimsFrom(t.id_token),
    };
  }

  return {
    async signIn(options) {
      const verifier = randomToken(48);
      const state = randomToken(16);
      const tx: Transaction = { verifier, state, returnTo: options?.returnTo ?? "/" };
      storage?.setItem(TX_KEY, JSON.stringify(tx));
      const params = new URLSearchParams({
        response_type: "code",
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        scope: scopes.join(" "),
        state,
        code_challenge: await pkceChallenge(verifier),
        code_challenge_method: "S256",
      });
      navigate(`${config.authority}/oauth2/authorize?${params.toString()}`);
    },

    handleCallback(url = typeof window !== "undefined" ? window.location.href : "") {
      // Idempotent per URL: React StrictMode runs the callback effect twice, and the authorization code works once.
      if (callback?.url !== url) callback = { url, result: completeCallback(url) };
      return callback.result;
    },
    async getSession() {
      if (!session) return null;
      if (session.expiresAt - REFRESH_SKEW_MS > Date.now()) return session;
      if (!session.refreshToken) {
        commit(null);
        return null;
      }
      refreshing ??= exchange({ grant_type: "refresh_token", refresh_token: session.refreshToken })
        .then((next) => {
          commit(next);
          return next;
        })
        .catch((e: unknown) => {
          // Only a refused grant ends the session. A network error or a 5xx keeps it, so a dropped connection
          // in the middle of a class does not bounce the learner to the sign-in page.
          if (e instanceof TokenError && e.status === 400) {
            commit(null);
            return null;
          }
          return session && session.expiresAt > Date.now() ? session : null;
        })
        .finally(() => {
          refreshing = null;
        });
      return refreshing;
    },

    async signOut() {
      // Clear without notifying: a "signed out" broadcast makes RequireAuth start a new sign-in, whose navigation
      // would cancel the one to /logout and sign the user straight back in.
      session = null;
      try {
        storage?.removeItem(SESSION_KEY);
        storage?.removeItem(TX_KEY);
      } catch {
        /* ignore */
      }
      const params = new URLSearchParams({ client_id: config.clientId, logout_uri: config.postLogoutRedirectUri });
      navigate(`${config.authority}/logout?${params.toString()}`);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  async function completeCallback(url: string): Promise<{ returnTo: string }> {
      const u = new URL(url);
      const error = u.searchParams.get("error");
      if (error) throw new Error(`sign-in failed: ${error}`);
      const code = u.searchParams.get("code");
      const state = u.searchParams.get("state");
      const raw = storage?.getItem(TX_KEY);
      storage?.removeItem(TX_KEY);
      if (!code || !state || !raw) throw new Error("no sign-in in progress");
      const tx = JSON.parse(raw) as Transaction;
      if (tx.state !== state) throw new Error("state mismatch");
      const next = await exchange({ grant_type: "authorization_code", code, redirect_uri: config.redirectUri, code_verifier: tx.verifier });
      commit(next);
      return { returnTo: tx.returnTo.startsWith("/") ? tx.returnTo : "/" };
  }
}

/** The token endpoint refused: `status` 400 with `invalid_grant` means the refresh token is revoked or expired. */
export class TokenError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`token endpoint returned ${status}${code ? ` (${code})` : ""}`);
  }
}

